import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor, registerPlugin } from '@capacitor/core'

type WidgetRole = 'member' | 'helper'

export type NativeWidgetAction =
  | 'member_pulse'
  | 'member_controls'
  | 'member_broadcast'
  | 'member_vouch'
  | 'member_voice'
  | 'helper_tasks'
  | 'helper_scan'
  | 'helper_proof'
  | 'helper_objective'

export type NativeWidgetIntent = {
  action: NativeWidgetAction
  targetId?: string
  workspaceId?: string
  timestamp?: number
}

type TimeliningWidgetBridge = {
  saveWidgetCache(input: { role: WidgetRole; payload: string }): Promise<{ saved: boolean }>
  getWidgetIntent(): Promise<{ payload?: string }>
}

const bridge = registerPlugin<TimeliningWidgetBridge>('TimeliningWidgetBridge')

export const isNativeWidgetBridgeAvailable = (): boolean => Capacitor.isNativePlatform()

export const saveNativeWidgetCache = async (role: WidgetRole, payload: unknown) => {
  if (!isNativeWidgetBridgeAvailable()) return
  try {
    await bridge.saveWidgetCache({ role, payload: JSON.stringify(payload) })
  } catch {
    // Native widget cache is best-effort; the app must never fail because a
    // launcher rejected or delayed a widget update.
  }
}

export const consumeNativeWidgetIntent = async (): Promise<NativeWidgetIntent | null> => {
  if (!isNativeWidgetBridgeAvailable()) return null
  try {
    const result = await bridge.getWidgetIntent()
    if (!result.payload) return null
    const parsed = JSON.parse(result.payload) as Partial<NativeWidgetIntent>
    return typeof parsed.action === 'string'
      ? {
          action: parsed.action as NativeWidgetAction,
          targetId: typeof parsed.targetId === 'string' ? parsed.targetId : undefined,
          workspaceId: typeof parsed.workspaceId === 'string' ? parsed.workspaceId : undefined,
          timestamp: typeof parsed.timestamp === 'number' ? parsed.timestamp : undefined,
        }
      : null
  } catch {
    return null
  }
}

export const onNativeWidgetResume = (callback: () => void): (() => void) => {
  if (!isNativeWidgetBridgeAvailable()) return () => {}
  let active = true
  const handles: Array<{ remove: () => Promise<void> }> = []
  void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (active && isActive) callback()
  }).then((handle) => handles.push(handle))
  void CapacitorApp.addListener('resume', () => {
    if (active) callback()
  }).then((handle) => handles.push(handle))
  return () => {
    active = false
    handles.forEach((handle) => { void handle.remove() })
  }
}
